import { router } from '@inertiajs/react';
import { format, formatDistanceToNow } from 'date-fns';
import {
  Check,
  ChevronDown,
  ExternalLink,
  Gamepad2,
  Loader2,
  Mail,
  MessageCircle,
  User,
  UserCheck,
  UserMinus,
  UserRound,
} from 'lucide-react';
import { useState } from 'react';
import { Linkified } from '@/components/linkified';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { getCountryDisplay } from '@/lib/countries';
import { cn } from '@/lib/utils';
import type {
  HackerLlmEvaluation,
  SubmissionMember,
  SubmissionStatus,
} from '@/types';
import { baseError } from '../lib/inertia-errors';
import { HackerEvaluation } from './TeamLlmEvaluations';

const ATTENDING_SUBMISSION_STATUSES: readonly SubmissionStatus[] = [
  'approved',
  'onboarding_request',
  'onboarding_expired',
  'onboarding_complete',
];

interface TeamMembersProps {
  submissionId: string;
  eventSlug: string;
  submissionStatus: SubmissionStatus;
  isAdminFull?: boolean;
  evaluations?: Map<string, HackerLlmEvaluation>;
  members: SubmissionMember[];
}

const getInitials = (name: string) => {
  const parts = name.split(' ');
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

const getGitHubAvatarUrl = (githubUsername: string | null) => {
  if (!githubUsername) return null;
  const username = githubUsername.replace(
    /^https?:\/\/(www\.)?github\.com\//,
    '',
  );
  return `https://github.com/${username}.png`;
};

export function TeamMembers({
  submissionId,
  eventSlug,
  submissionStatus,
  isAdminFull = false,
  evaluations,
  members,
}: TeamMembersProps) {
  const canWithdrawMembers =
    ATTENDING_SUBMISSION_STATUSES.includes(submissionStatus);
  const base = `/admin/${eventSlug}/submissions/${submissionId}`;
  const [expandedNotes, setExpandedNotes] = useState<Record<string, boolean>>(
    {},
  );
  const [expandedOnboarding, setExpandedOnboarding] = useState<
    Record<string, boolean>
  >({});
  const [noteInputs, setNoteInputs] = useState<Record<string, string>>({});
  const [pendingHackerId, setPendingHackerId] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [impersonatingHackerId, setImpersonatingHackerId] = useState<
    string | null
  >(null);
  const [withdrawTarget, setWithdrawTarget] = useState<{
    hackerId: string;
    fullName: string;
  } | null>(null);
  const [isWithdrawing, setIsWithdrawing] = useState(false);
  const [withdrawError, setWithdrawError] = useState<string | null>(null);

  const handleWithdrawConfirm = () => {
    if (!withdrawTarget) return;
    setWithdrawError(null);
    const hackerId = withdrawTarget.hackerId;
    router.post(
      `${base}/withdraw-member`,
      { hackerId },
      {
        preserveScroll: true,
        onStart: () => setIsWithdrawing(true),
        onFinish: () => setIsWithdrawing(false),
        onSuccess: () => setWithdrawTarget(null),
        onError: (responseErrors) =>
          setWithdrawError(
            baseError(responseErrors) ?? 'Failed to withdraw member',
          ),
      },
    );
  };

  const toggleNoteExpansion = (hackerId: string) => {
    setExpandedNotes((prev) => ({
      ...prev,
      [hackerId]: !prev[hackerId],
    }));
  };

  const handleNoteSubmit = (
    hackerId: string,
    e: React.FormEvent | React.KeyboardEvent,
  ) => {
    e.preventDefault();
    const noteBody = noteInputs[hackerId]?.trim();
    if (!noteBody || pendingHackerId) return;

    setErrors((prev) => ({ ...prev, [hackerId]: '' }));
    setPendingHackerId(hackerId);

    const noteToSubmit = noteBody;
    setNoteInputs((prev) => ({ ...prev, [hackerId]: '' }));

    router.post(
      `${base}/hacker-notes`,
      { hackerId, body: noteToSubmit },
      {
        preserveScroll: true,
        onFinish: () => setPendingHackerId(null),
        onSuccess: () => {
          // Collapse the note input after successful submission
          setExpandedNotes((prev) => ({ ...prev, [hackerId]: false }));
        },
        onError: (responseErrors) => {
          setErrors((prev) => ({
            ...prev,
            [hackerId]: baseError(responseErrors) ?? 'Failed to add note',
          }));
          setNoteInputs((prev) => ({ ...prev, [hackerId]: noteToSubmit }));
        },
      },
    );
  };

  const handleKeyDown = (
    hackerId: string,
    e: React.KeyboardEvent<HTMLTextAreaElement>,
  ) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleNoteSubmit(hackerId, e);
    }
  };

  const handleImpersonate = (hackerId: string) => {
    setImpersonatingHackerId(hackerId);
    router.post(
      `${base}/impersonate`,
      { hackerId },
      {
        onError: (responseErrors) => {
          alert(baseError(responseErrors) ?? 'Failed to impersonate user');
          setImpersonatingHackerId(null);
        },
      },
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Members ({members.length})</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {members.map((member) => (
            <div
              key={member.id}
              className={cn(
                'rounded-lg border bg-card p-4 transition-opacity',
                member.profile?.memberStatus === 'withdrawn' &&
                  'opacity-50 grayscale',
              )}
            >
              <div className="flex items-start gap-3">
                <Avatar className="h-12 w-12">
                  <AvatarImage
                    src={getGitHubAvatarUrl(member.github) || undefined}
                    alt={member.fullName}
                  />
                  <AvatarFallback>
                    {getInitials(member.fullName)}
                  </AvatarFallback>
                </Avatar>

                <div className="flex-1 space-y-1">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="font-medium">{member.fullName}</div>
                      {member.profile?.isVeteran && (
                        <Badge variant="secondary" className="text-xs">
                          Veteran
                        </Badge>
                      )}
                      {member.gender === 'female' && (
                        <Badge
                          variant="outline"
                          className="border-pink-300 bg-pink-100 text-pink-800 text-xs"
                        >
                          <UserRound className="mr-1 h-3 w-3" />
                          Woman
                        </Badge>
                      )}
                      {member.arcadeGame && (
                        <a
                          href={member.arcadeGame.repoUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Open the game"
                        >
                          <Badge
                            variant="outline"
                            className="cursor-pointer border-violet-300 bg-violet-100 text-violet-800 text-xs hover:bg-violet-200"
                          >
                            <Gamepad2 className="mr-1 h-3 w-3" />
                            {member.arcadeGame.repoName}
                          </Badge>
                        </a>
                      )}
                      {member.previousEvents.map((evt) => (
                        <a
                          key={`attended-${evt.slug}`}
                          href={`/admin/submissions/${evt.submissionId}`}
                          className="hover:underline"
                        >
                          <Badge
                            variant="outline"
                            className="border-blue-200 bg-blue-50 text-blue-700 text-xs"
                          >
                            Hack {evt.slug.toUpperCase()}
                          </Badge>
                        </a>
                      ))}
                      {member.previousApplications
                        .filter(
                          (app) =>
                            !member.previousEvents.some(
                              (e) => e.slug === app.slug,
                            ),
                        )
                        .map((app) => (
                          <a
                            key={`applied-${app.slug}`}
                            href={`/admin/submissions/${app.submissionId}`}
                            className="hover:underline"
                          >
                            <Badge
                              variant="outline"
                              className="border-purple-200 bg-purple-50 text-purple-700 text-xs"
                            >
                              Hack {app.slug.toUpperCase()} Applicant
                            </Badge>
                          </a>
                        ))}
                      {member.profile?.onboardCompleteAt && (
                        <Badge
                          variant="outline"
                          className="border-green-200 bg-green-50 text-green-700 text-xs"
                        >
                          Onboarded 🚢
                        </Badge>
                      )}
                      {member.profile?.memberStatus === 'withdrawn' && (
                        <Badge
                          variant="outline"
                          className="border-red-200 bg-red-50 text-red-700 text-xs"
                        >
                          Withdrawn
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      {isAdminFull && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleImpersonate(member.id)}
                          disabled={impersonatingHackerId === member.id}
                          className="h-7 gap-1 text-xs"
                        >
                          {impersonatingHackerId === member.id ? (
                            <>
                              <Loader2 className="h-3 w-3 animate-spin" />
                              Impersonating...
                            </>
                          ) : (
                            <>
                              <UserCheck className="h-3 w-3" />
                              Impersonate
                            </>
                          )}
                        </Button>
                      )}
                      {isAdminFull &&
                        canWithdrawMembers &&
                        member.profile?.memberStatus !== 'withdrawn' && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() =>
                              setWithdrawTarget({
                                hackerId: member.id,
                                fullName: member.fullName,
                              })
                            }
                            title="Mark as withdrawn"
                            className="h-7 w-7 text-muted-foreground hover:text-red-600"
                          >
                            <UserMinus className="h-3.5 w-3.5" />
                          </Button>
                        )}
                    </div>
                  </div>

                  <div className="flex items-center gap-1 text-muted-foreground text-sm">
                    <Mail className="h-3 w-3" />
                    <a
                      href={`mailto:${member.email}`}
                      className="hover:text-foreground hover:underline"
                    >
                      {member.email}
                    </a>
                  </div>

                  <div className="flex gap-3 text-sm">
                    {member.github && (
                      <a
                        href={
                          member.github.startsWith('http')
                            ? member.github
                            : `https://github.com/${member.github}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-muted-foreground hover:text-foreground"
                      >
                        GitHub
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    )}

                    {member.linkedin && (
                      <a
                        href={
                          member.linkedin.startsWith('http')
                            ? member.linkedin
                            : `https://linkedin.com/in/${member.linkedin}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-muted-foreground hover:text-foreground"
                      >
                        LinkedIn
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                </div>
              </div>

              {member.profile && (
                <>
                  <Separator className="my-3" />
                  <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                    {member.profile.age && (
                      <div>
                        <span className="font-medium text-muted-foreground">
                          Age:
                        </span>{' '}
                        <span>{member.profile.age}</span>
                      </div>
                    )}

                    {member.profile.country && (
                      <div>
                        <span className="font-medium text-muted-foreground">
                          Country:
                        </span>{' '}
                        <span>{getCountryDisplay(member.profile.country)}</span>
                      </div>
                    )}

                    {member.profile.education && (
                      <div className="sm:col-span-2">
                        <span className="font-medium text-muted-foreground">
                          Education:
                        </span>{' '}
                        <span>
                          <Linkified text={member.profile.education} />
                        </span>
                      </div>
                    )}

                    {member.profile.previousHackathons && (
                      <div className="sm:col-span-2">
                        <span className="font-medium text-muted-foreground">
                          Previous Hackathons:
                        </span>{' '}
                        <span>{member.profile.previousHackathons}</span>
                      </div>
                    )}

                    {member.profile.bio && (
                      <div className="sm:col-span-2">
                        <span className="font-medium text-muted-foreground">
                          Bio:
                        </span>{' '}
                        <p className="mt-1 text-xs">
                          <Linkified text={member.profile.bio} />
                        </p>
                      </div>
                    )}

                    {member.profile.shirtSize && (
                      <div>
                        <span className="font-medium text-muted-foreground">
                          Shirt Size:
                        </span>{' '}
                        <span>{member.profile.shirtSize}</span>
                      </div>
                    )}

                    {member.profile.diet && (
                      <div>
                        <span className="font-medium text-muted-foreground">
                          Diet:
                        </span>{' '}
                        <span>{member.profile.diet}</span>
                      </div>
                    )}

                    {member.profile.allergies && (
                      <div className="sm:col-span-2">
                        <span className="font-medium text-muted-foreground">
                          Allergies:
                        </span>{' '}
                        <span className="text-red-600">
                          {member.profile.allergies}
                        </span>
                      </div>
                    )}

                    {member.profile.physicalIssues && (
                      <div className="sm:col-span-2">
                        <span className="font-medium text-muted-foreground">
                          Physical Issues:
                        </span>{' '}
                        <span className="text-orange-600">
                          {member.profile.physicalIssues}
                        </span>
                      </div>
                    )}

                    {member.profile.shareInfoWithSponsors && (
                      <div className="sm:col-span-2">
                        <span className="flex items-center gap-1 text-muted-foreground text-sm">
                          <Check className="h-3 w-3 text-green-600" />
                          <span>Willing to share info with sponsors</span>
                        </span>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Onboarding Information */}
              {member.profile?.onboardCompleteAt && (
                <>
                  <Separator className="my-3" />
                  <div className="space-y-3">
                    <button
                      type="button"
                      onClick={() =>
                        setExpandedOnboarding((prev) => ({
                          ...prev,
                          [member.id]: !prev[member.id],
                        }))
                      }
                      className="flex w-full items-center justify-between font-medium text-sm hover:text-foreground"
                    >
                      <span>Onboarding Information</span>
                      <ChevronDown
                        className={`h-4 w-4 text-muted-foreground transition-transform ${
                          expandedOnboarding[member.id] ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    {expandedOnboarding[member.id] && (
                      <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                        {member.profile.termsAcceptedAt && (
                          <div>
                            <span className="font-medium text-muted-foreground">
                              Terms Accepted:
                            </span>{' '}
                            <span>
                              {format(
                                new Date(member.profile.termsAcceptedAt),
                                'MMM d, yyyy',
                              )}
                            </span>
                          </div>
                        )}

                        {member.profile.onboardCompleteAt && (
                          <div>
                            <span className="font-medium text-muted-foreground">
                              Onboarding Completed:
                            </span>{' '}
                            <span>
                              {format(
                                new Date(member.profile.onboardCompleteAt),
                                'MMM d, yyyy',
                              )}
                            </span>
                          </div>
                        )}

                        {member.profile.discordUsername && (
                          <div>
                            <span className="font-medium text-muted-foreground">
                              Discord:
                            </span>{' '}
                            <span className="font-mono">
                              {member.profile.discordUsername}
                            </span>
                          </div>
                        )}

                        {member.profile.discordConnectedAt && (
                          <div>
                            <span className="font-medium text-muted-foreground">
                              Discord Connected:
                            </span>{' '}
                            <span>
                              {format(
                                new Date(member.profile.discordConnectedAt),
                                'MMM d, yyyy',
                              )}
                            </span>
                          </div>
                        )}

                        {member.profile.anthropicAccountEmail && (
                          <div className="sm:col-span-2">
                            <span className="font-medium text-muted-foreground">
                              Anthropic Account:
                            </span>{' '}
                            <span>{member.profile.anthropicAccountEmail}</span>
                          </div>
                        )}

                        {member.profile.anthropicOrgId && (
                          <div className="sm:col-span-2">
                            <span className="font-medium text-muted-foreground">
                              Anthropic Org ID:
                            </span>{' '}
                            <span className="font-mono text-xs">
                              {member.profile.anthropicOrgId}
                            </span>
                          </div>
                        )}

                        {member.profile.anthropicUsedProducts &&
                          member.profile.anthropicUsedProducts.length > 0 && (
                            <div className="sm:col-span-2">
                              <span className="font-medium text-muted-foreground">
                                Anthropic Products Used:
                              </span>{' '}
                              <span>
                                {member.profile.anthropicUsedProducts.join(
                                  ', ',
                                )}
                              </span>
                            </div>
                          )}

                        {member.profile.anthropicUpdates !== null && (
                          <div className="sm:col-span-2">
                            <span className="flex items-center gap-1 text-sm">
                              {member.profile.anthropicUpdates ? (
                                <>
                                  <Check className="h-3 w-3 text-green-600" />
                                  <span>Subscribed to Anthropic updates</span>
                                </>
                              ) : (
                                <span className="text-muted-foreground">
                                  Not subscribed to Anthropic updates
                                </span>
                              )}
                            </span>
                          </div>
                        )}

                        {member.profile.nationalId && (
                          <div>
                            <span className="font-medium text-muted-foreground">
                              National ID:
                            </span>{' '}
                            <span className="font-mono">
                              {member.profile.nationalId}
                            </span>
                          </div>
                        )}

                        {member.profile.shoeSize && (
                          <div>
                            <span className="font-medium text-muted-foreground">
                              Shoe Size:
                            </span>{' '}
                            <span>{member.profile.shoeSize}</span>
                          </div>
                        )}

                        {member.profile.emergencyContactName && (
                          <div>
                            <span className="font-medium text-muted-foreground">
                              Emergency Contact:
                            </span>{' '}
                            <span>{member.profile.emergencyContactName}</span>
                          </div>
                        )}

                        {member.profile.emergencyContactPhone && (
                          <div>
                            <span className="font-medium text-muted-foreground">
                              Emergency Phone:
                            </span>{' '}
                            <span>{member.profile.emergencyContactPhone}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Notes section - always visible */}
              {member.notes.length > 0 && (
                <div className="mt-3 border-t pt-3">
                  <div className="space-y-2">
                    {member.notes.map((note) => (
                      <div
                        key={note.id}
                        className="space-y-2 rounded-lg border-2 border-primary/20 bg-primary/5 p-3 shadow-sm"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-2 text-muted-foreground text-xs">
                            <User className="h-3 w-3" />
                            {note.author.fullName}
                          </div>
                          <div className="text-muted-foreground text-xs">
                            {formatDistanceToNow(new Date(note.createdAt), {
                              addSuffix: true,
                            })}
                          </div>
                        </div>
                        <div className="whitespace-pre-wrap text-sm">
                          <Linkified text={note.body} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Conversation bubble and add note input */}
              <div className="mt-3 border-t pt-3">
                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => toggleNoteExpansion(member.id)}
                    className="relative flex items-center gap-1 text-muted-foreground text-sm hover:text-foreground"
                  >
                    <MessageCircle className="h-4 w-4" />
                    <span>
                      {expandedNotes[member.id] ? 'Hide input' : 'Add note'}
                    </span>
                  </button>
                </div>

                {expandedNotes[member.id] && (
                  <div className="mt-3 space-y-2">
                    <div className="relative">
                      <Textarea
                        placeholder="Add a note about this hacker... (Press Enter to submit, Shift+Enter for new line)"
                        value={noteInputs[member.id] || ''}
                        onChange={(e) =>
                          setNoteInputs((prev) => ({
                            ...prev,
                            [member.id]: e.target.value,
                          }))
                        }
                        onKeyDown={(e) => handleKeyDown(member.id, e)}
                        disabled={pendingHackerId === member.id}
                        className="min-h-[80px] resize-none pr-10"
                      />
                      {pendingHackerId === member.id && (
                        <div className="absolute top-3 right-3">
                          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                        </div>
                      )}
                    </div>
                    {errors[member.id] && (
                      <p className="text-red-600 text-sm">
                        {errors[member.id]}
                      </p>
                    )}
                  </div>
                )}
              </div>
              {evaluations?.get(member.id) && (
                <>
                  <Separator className="my-3" />
                  <HackerEvaluation
                    member={{
                      hackerId: member.id,
                      fullName: member.fullName,
                      github: member.github,
                      linkedin: member.linkedin,
                      evaluation: evaluations.get(member.id) ?? null,
                    }}
                  />
                </>
              )}
            </div>
          ))}
        </div>
      </CardContent>
      <AlertDialog
        open={withdrawTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setWithdrawTarget(null);
            setWithdrawError(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark member as withdrawn?</AlertDialogTitle>
            <AlertDialogDescription>
              {withdrawTarget?.fullName} will be marked as withdrawn from this
              submission. The submission itself stays in its current status.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {withdrawError && (
            <p className="text-red-600 text-sm">{withdrawError}</p>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isWithdrawing}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleWithdrawConfirm();
              }}
              disabled={isWithdrawing}
            >
              {isWithdrawing ? 'Withdrawing...' : 'Withdraw'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
